import { useEffect, useRef, useState } from 'react';
import { gameEvents, EVT, type BastetDialogPayload } from '../game/events';
import { BASTET_DIALOG_RU } from '@cradle7/shared';

/**
 * Phase 2h — Bastet boss dialog overlay.
 *
 * Subscribes to `gameEvents.BastetDialog` and shows a typewriter-style
 * monologue at the bottom-center of the viewport. Tap to skip / advance /
 * dismiss. Mounted only inside the in-run UI tree (see `App.tsx`).
 */

const CHAR_INTERVAL_MS = 30;

type ActiveDialog = {
  phase: 1 | 2 | 'defeat';
  lines: readonly string[];
};

function linesFor(phase: BastetDialogPayload['phase']): readonly string[] {
  if (phase === 1) return BASTET_DIALOG_RU.intro;
  if (phase === 2) return BASTET_DIALOG_RU.transition;
  return BASTET_DIALOG_RU.defeat;
}

export function BastetDialogOverlay() {
  const [active, setActive] = useState<ActiveDialog | null>(null);
  const [lineIndex, setLineIndex] = useState(0);
  const [charsRevealed, setCharsRevealed] = useState(0);
  const tickRef = useRef<number | null>(null);

  // Subscribe to the typed gameEvents bus.
  useEffect(() => {
    const handler = (payload: BastetDialogPayload) => {
      setActive({ phase: payload.phase, lines: linesFor(payload.phase) });
      setLineIndex(0);
      setCharsRevealed(0);
    };
    gameEvents.on(EVT.BastetDialog, handler);
    return () => {
      gameEvents.off(EVT.BastetDialog, handler);
    };
  }, []);

  // Drive the typewriter via window.setInterval so vitest fake timers can
  // advance it deterministically.
  useEffect(() => {
    if (!active) return;
    const currentLine = active.lines[lineIndex] ?? '';
    if (charsRevealed >= currentLine.length) return;

    const id = window.setInterval(() => {
      setCharsRevealed((c) => {
        const next = c + 1;
        if (next >= currentLine.length) {
          if (tickRef.current !== null) {
            window.clearInterval(tickRef.current);
            tickRef.current = null;
          }
          return currentLine.length;
        }
        return next;
      });
    }, CHAR_INTERVAL_MS);
    tickRef.current = id;
    return () => {
      window.clearInterval(id);
      tickRef.current = null;
    };
  }, [active, lineIndex, charsRevealed]);

  if (!active) return null;

  const currentLine = active.lines[lineIndex] ?? '';
  const isTyping = charsRevealed < currentLine.length;
  const isLast = lineIndex >= active.lines.length - 1;

  function onTap() {
    if (!active) return;
    if (isTyping) {
      // Skip to end of current line.
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      setCharsRevealed(currentLine.length);
      return;
    }
    if (!isLast) {
      setLineIndex((i) => i + 1);
      setCharsRevealed(0);
      return;
    }
    // Last line revealed and tapped — dismiss.
    setActive(null);
    setLineIndex(0);
    setCharsRevealed(0);
  }

  const shown = currentLine.slice(0, charsRevealed);

  return (
    <div
      data-testid="bastet-overlay"
      onClick={onTap}
      style={{
        position: 'fixed',
        left: '50%',
        bottom: '6vh',
        transform: 'translateX(-50%)',
        width: 'min(92vw, 32rem)',
        zIndex: 50,
        cursor: 'pointer',
        padding: 'var(--space-4) var(--space-5)',
        background:
          'linear-gradient(180deg, rgba(8, 6, 16, 0.94) 0%, rgba(2, 1, 8, 0.96) 100%)',
        border: '1px solid var(--gold-hi)',
        borderRadius: '4px',
        boxShadow:
          '0 0 0 1px rgba(0, 0, 0, 0.6) inset, 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 24px var(--gold-shadow)',
        userSelect: 'none',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--f-display)',
          fontSize: '0.85rem',
          letterSpacing: '0.18em',
          color: 'var(--gold-hi)',
          textShadow: '0 0 6px var(--gold-shadow)',
          marginBottom: 'var(--space-2)',
        }}
      >
        ◆ БАСТЕТ
      </div>
      <div
        data-testid="bastet-line"
        style={{
          fontFamily: 'var(--f-body)',
          fontStyle: 'italic',
          fontSize: '1rem',
          lineHeight: 1.5,
          color: '#f5e9c2',
          minHeight: '3rem',
        }}
      >
        {shown}
        {isTyping && (
          <span
            className="blink"
            style={{ color: 'var(--gold-hi)', marginLeft: 2 }}
          >
            ▌
          </span>
        )}
      </div>
      <div
        style={{
          marginTop: 'var(--space-2)',
          fontSize: '0.6rem',
          letterSpacing: '0.2em',
          color: 'var(--gold-deep)',
          textAlign: 'right',
          opacity: isTyping ? 0.4 : 0.85,
        }}
      >
        {isTyping
          ? '// TAP TO SKIP'
          : isLast
            ? '// TAP TO DISMISS'
            : '// TAP TO CONTINUE'}
      </div>
    </div>
  );
}
